import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { every, some, debounce, times } from 'lodash';

import UltimatePagination from 'react-ultimate-pagination-bootstrap-4';

import MapListHeader from 'bundles/research/index/components/map_list_header';
import MapListItem from 'bundles/research/index/components/map_list_item';
import SelectionDetails from 'components/selection_details';

import { selectCompMarker } from './store/actions/ui';
import {
  resetSelectedComps,
  resetSelectedCompsOnCurrentPage,
  toggleSelectComp,
  toggleSelectAllComps,
  toggleSelectAllCompsOnCurrentPage
} from './store/actions/select_list';

import { goToCompShow } from './store/actions/comps';
import { getCurrentTab } from 'bundles/comps/index/store/selectors/ui';
import 'styles/semantic-ui-placeholder.scss';
import classNames from 'classnames';

export class CompsMapList extends React.Component {
  static propTypes = {
    compIds: PropTypes.array.isRequired,
    compLeases: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
    })).isRequired,
    compSales: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
    })).isRequired,
    currentPage: PropTypes.number.isRequired,
    currentTab: PropTypes.oneOf(['lease', 'sale']).isRequired,
    features: PropTypes.shape({
      edgeCrm: PropTypes.bool,
    }).isRequired,
    googleMapsApiKey: PropTypes.string,
    goToCompShow: PropTypes.func.isRequired,
    isFetchingComps: PropTypes.bool,
    resetSelection: PropTypes.func.isRequired,
    resetSelectionOnCurrentPage: PropTypes.func.isRequired,
    selectedLeaseIds: PropTypes.array.isRequired,
    selectedSaleIds: PropTypes.array.isRequired,
    showMapList: PropTypes.bool.isRequired,
    toggleSelectAll: PropTypes.func.isRequired,
    toggleSelectAllOnCurrentPage: PropTypes.func.isRequired,
    toggleSelectComp: PropTypes.func.isRequired,
    totalCount: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    onCompHover: PropTypes.func.isRequired,
    onPageChange: PropTypes.func.isRequired
  };

  getComps() {
    const { currentTab, compLeases, compSales } = this.props;
    return currentTab === 'lease' ? compLeases : compSales;
  }

  getSelectedCompIds() {
    const { currentTab, selectedLeaseIds, selectedSaleIds } = this.props;
    return currentTab === 'lease' ? selectedLeaseIds : selectedSaleIds;
  }

  isSelected(compId) {
    return this.getSelectedCompIds().indexOf(compId) > -1;
  }

  allSelected() {
    return every(this.getComps(), comp => this.isSelected(comp.id)) && this.getComps().length > 0;
  }

  someSelected() {
    return some(this.getComps(), comp => this.isSelected(comp.id)) && this.getComps().length > 0;
  }

  throttledHover = debounce(this.props.onCompHover, 250);

  render() {
    const { isFetchingComps, showMapList } = this.props;

    return (
      <div className="bg-body border-end flex-column d-flex h-100">
        {this.renderTableHeader()}
        <div className="flex-grow-1 overflow-auto">
          {isFetchingComps ? this.renderPlaceholder() : this.renderComps()}
        </div>
      </div>
    );
  }

  renderTableHeader() {
    const { resetSelectionOnCurrentPage, toggleSelectAllOnCurrentPage } = this.props;
    const isIndeterminate = this.someSelected() && !this.allSelected();
    const onCheckboxChangeHandler = isIndeterminate => (
      isIndeterminate ? resetSelectionOnCurrentPage : toggleSelectAllOnCurrentPage
    );

    return (
      <MapListHeader
        allSelected={this.allSelected()}
        edgeCrm={this.props.features.edgeCrm}
        isIndeterminate={isIndeterminate}
        selectionCount={this.getSelectedCompIds().length}
        toggleSelectAll={onCheckboxChangeHandler(isIndeterminate)}
        totalCount={this.props.totalCount}
        totalLabel="comp"
      />
    );
  }

  renderPlaceholder() {
    const placeholders = times(8, i => (
      <div className="card" key={`placeholder-${i}`}>
        <div className="card-body">
          <div className="d-flex flex-column gap-1">
            <div className="placeholder rounded" />
            <div className="placeholder rounded w-75" />
            <div className="placeholder rounded w-50" />
          </div>
        </div>
      </div>
    ));

    return (
      <div className={classNames("d-flex flex-column", { "gap-2 p-3": this.props.features.edgeCrm })}>
        {placeholders}
      </div>
    );
  }

  renderComps() {
    const {
      currentPage,
      totalPages,
      onPageChange,
      totalCount,
    } = this.props;
    const comps = this.getComps();

    return (
      <>
        <SelectionDetails
          label={this.props.currentTab}
          selectedCount={this.getSelectedCompIds().length}
          totalCount={this.props.compIds.length}
          onClearSelection={this.props.resetSelection}
          onSelectAll={this.props.toggleSelectAll}
        />
        <div className={classNames("d-flex flex-column", { "gap-2 p-3": this.props.features.edgeCrm })}>
          {comps.map(comp => (
            <MapListItem
              edgeCrm={this.props.features.edgeCrm}
              googleMapsApiKey={this.props.googleMapsApiKey}
              isSelected={this.isSelected(comp.id)}
              key={comp.id}
              record={comp}
              subtitleKey="addressOneLine"
              onClick={event => this.props.goToCompShow(event, comp)}
              onMouseOver={() => this.throttledHover(comp.id)}
              onSelectionToggle={() => this.props.toggleSelectComp(comp)}
            />
          ))}
          {comps.length < totalCount &&
          <div className="text-center">
            <UltimatePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onChange={onPageChange}
            />
          </div>
          }
        </div>
      </>
    );
  }
}

const mapStateToProps = state => ({
  ...state.comps,
  ...state.search.meta,

  compIds: state.search.meta.compIds,
  googleMapsApiKey: state.ui.googleMapsApiKey,
  isFetchingComps: state.search.isFetching,
  compSales: state.search.sales,
  compLeases: state.search.leases,
  currentTab: getCurrentTab(state),
  features: state.features,
  selectedSaleIds: state.selectList.selectedSaleIds,
  selectedLeaseIds: state.selectList.selectedLeaseIds,
  showMapList: state.ui.showMapList
});

const mapDispatchToProps = {
  goToCompShow,
  toggleSelectComp,

  resetSelection: resetSelectedComps,
  resetSelectionOnCurrentPage: resetSelectedCompsOnCurrentPage,
  toggleSelectAll: toggleSelectAllComps,
  toggleSelectAllOnCurrentPage: toggleSelectAllCompsOnCurrentPage,
  onCompHover: selectCompMarker,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompsMapList);
